import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { orderBy } from 'lodash';
import {
  Avatar,
  Cushion,
  FlexContainer,
  LinearGradient,
  Rectangle,
  Shadow,
  Space,
  Switch,
  Typography,
} from '@pitchero/react-ui';
import { connect } from 'react-redux';
import Icon from '../icon';
import { openLoginPanel as openLoginPanelAction } from '../../store/auth/actions';
import { PLACEHOLDER_AVATAR } from '../../lib/club-theme/avatar';
import { Trans } from '../../lib/i18n';
import { getPrimaryMemberId, getLoggedInUser } from '../../store/membership/selectors';
import JoinClubPanel from './join-club-panel';
import EventAttendanceLoadingState from './event-attendance-loading-state';

const EventAttendance = ({
  openLoginPanel,
  isLoading,
  isLoggedIn,
  memberAttendances,
  onMarkAsAttending,
  onMarkAsNotAttending,
  primaryMemberId,
  userIsAssociatedToClub,
  user,
}) => {
  const [joinPanelIsOpen, setJoinPanelIsOpen] = useState(false);

  if (isLoading) {
    return <EventAttendanceLoadingState />;
  }

  if (isLoggedIn && userIsAssociatedToClub && primaryMemberId) {
    const orderedAttendances = orderBy(
      memberAttendances,
      [(attendees) => (attendees.memberId === primaryMemberId ? 1 : 0), 'name'],
      ['desc', 'asc'],
    );

    return orderedAttendances.map((member, index) => {
      const bgColor = member.isAttending ? 'positive' : 'notification';
      return (
        <Shadow key={member.memberId}>
          <Space top={index > 0 ? 'medium' : 0}>
            <Rectangle fill="white" radius="rounded">
              <FlexContainer flexDirection="row" justifyContent="space-between" alignItems="center">
                <Rectangle radius="roundedLeft">
                  <LinearGradient
                    component="div"
                    angle={60}
                    stops={[
                      [0, bgColor],
                      ['50%', bgColor],
                      ['50%', '#fff'],
                    ]}
                  >
                    <Cushion horizontal="small" vertical="xsmall" component="div">
                      <Avatar size={40} image={member.avatar} alt={member.name} boxShadow="dark" />
                    </Cushion>
                  </LinearGradient>
                </Rectangle>
                <Cushion left="xsmall">
                  <Typography
                    preset="name"
                    component="div"
                    color="black"
                    align="left"
                    style={{ flex: 1 }}
                  >
                    <>
                      {member.memberId === primaryMemberId && (
                        <Trans i18nkey="common:are_you_attending">Are you attending?</Trans>
                      )}
                      {member.memberId !== primaryMemberId && (
                        <Trans
                          i18nkey="common:is_member_attending"
                          values={{ memberName: member.firstName }}
                        >
                          {`Is ${member.firstName} attending?`}
                        </Trans>
                      )}
                    </>
                  </Typography>
                </Cushion>
                <Cushion all="small" component="div">
                  <Switch
                    preset="success"
                    checked={member.isAttending}
                    onChange={() =>
                      member.isAttending ? onMarkAsNotAttending(member) : onMarkAsAttending(member)
                    }
                  />
                </Cushion>
              </FlexContainer>
            </Rectangle>
          </Space>
        </Shadow>
      );
    });
  }

  return (
    <>
      <Shadow>
        <Rectangle fill="white" radius="rounded">
          <FlexContainer
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            onClick={() => (isLoggedIn ? setJoinPanelIsOpen(true) : openLoginPanel())}
            style={{ cursor: 'pointer' }}
          >
            <Rectangle radius="roundedLeft">
              <LinearGradient
                component="div"
                angle={60}
                stops={[
                  [0, 'notification'],
                  ['50%', 'notification'],
                  ['50%', '#fff'],
                ]}
              >
                <Cushion horizontal="small" vertical="xsmall" component="div">
                  <Avatar
                    size={40}
                    image={user ? user.avatar : PLACEHOLDER_AVATAR}
                    alt=""
                    boxShadow="dark"
                  />
                </Cushion>
              </LinearGradient>
            </Rectangle>
            <Cushion left="xsmall">
              <Typography
                preset="name"
                component="div"
                color="black"
                align="left"
                style={{ flex: 1 }}
              >
                <Trans i18nkey="common:are_you_attending">Are you attending?</Trans>
              </Typography>
            </Cushion>
            <Cushion vertical="small" right="medium" left="small" component="div">
              <Icon name="arrow-right" color="dustygrey" size={12} />
            </Cushion>
          </FlexContainer>
        </Rectangle>
      </Shadow>
      {!userIsAssociatedToClub && (
        <JoinClubPanel isOpen={joinPanelIsOpen} onClose={() => setJoinPanelIsOpen(false)} />
      )}
    </>
  );
};

EventAttendance.defaultProps = {
  primaryMemberId: null,
  user: null,
};

EventAttendance.propTypes = {
  openLoginPanel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  memberAttendances: PropTypes.arrayOf(
    PropTypes.shape({
      avatar: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      isAttending: PropTypes.bool.isRequired,
      memberId: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onMarkAsAttending: PropTypes.func.isRequired,
  onMarkAsNotAttending: PropTypes.func.isRequired,
  primaryMemberId: PropTypes.number,
  user: PropTypes.shape({
    avatar: PropTypes.string,
    name: PropTypes.string,
  }),
  userIsAssociatedToClub: PropTypes.bool.isRequired,
};

const mapStateToProps = (state, { club: { id: clubId } }) => ({
  primaryMemberId: getPrimaryMemberId(state, clubId),
  user: getLoggedInUser(state, clubId),
});

export default connect(mapStateToProps, { openLoginPanel: openLoginPanelAction })(EventAttendance);
